import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  clearSelectedVoucher,
  deleteVoucherCode,
  fetchVoucher,
  fetchVoucherCodes,
  updateVoucherStatus,
} from '../redux/slices/vouchersSlice';
import { fetchOrganizers } from '../redux/slices/organizersSlice';
import NoData from '../components/NoData';
import VoucherCodeDrawer from '../components/VoucherCodeDrawer';
import VoucherCodeRedemptionsTable from '../components/VoucherCodeRedemptionsTable';
import VoucherDrawer from '../components/VoucherDrawer';
import WarningDialog from '../components/WarningDialog';
import {
  formatVoucherDate,
  formatVoucherDiscount,
  getVoucherStatus,
  getVoucherStatusColor,
} from '../utils/voucherUtils';

const Detail = ({ label, value, helperText }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
    {helperText && (
      <Typography variant="caption" color="text.secondary">
        {helperText}
      </Typography>
    )}
  </Box>
);

export default function VoucherDetails() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { voucherId } = useParams();
  const user = useSelector((state) => state.auth.user);
  const voucher = useSelector((state) => state.vouchers.selectedVoucher);
  const codes = useSelector((state) => state.vouchers.codes);
  const isLoading = useSelector((state) => state.vouchers.isLoading);
  const isCodesLoading = useSelector((state) => state.vouchers.isCodesLoading);
  const organizers = useSelector((state) => state.organizers.data);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [codeDrawerOpen, setCodeDrawerOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [redemptionsCode, setRedemptionsCode] = useState(null);
  const [deleteCode, setDeleteCode] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([
          dispatch(fetchVoucher({ voucherId })).unwrap(),
          dispatch(fetchVoucherCodes({ voucherId })).unwrap(),
        ]);
      } catch {
        toast.error(t('Voucher could not be loaded'), { position: toast.POSITION.BOTTOM_RIGHT });
        navigate('/vouchers', { replace: true });
      }
    };

    load();
    if (user.role === 'admin') dispatch(fetchOrganizers());

    return () => {
      dispatch(clearSelectedVoucher());
    };
  }, [dispatch, navigate, t, user.role, voucherId]);

  const organizerName = useMemo(
    () =>
      user.role === 'organizer'
        ? user.organization
        : organizers.find(({ id }) => Number(id) === Number(voucher?.organizers_id))?.name || '-',
    [organizers, user.organization, user.role, voucher?.organizers_id]
  );

  if (isLoading && !voucher) {
    return (
      <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!voucher) return null;

  const voucherStatus = getVoucherStatus(voucher);
  const toggleVoucherStatus = async () => {
    setActionsAnchor(null);
    try {
      await dispatch(updateVoucherStatus({ voucherId, active: !Boolean(voucher.active) })).unwrap();
      toast.success(t(voucher.active ? 'Voucher deactivated successfully' : 'Voucher activated successfully'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch {
      toast.error(t('Voucher status could not be changed'), { position: toast.POSITION.BOTTOM_RIGHT });
    }
  };

  const handleCodeDrawerClose = () => {
    setCodeDrawerOpen(false);
    setSelectedCode(null);
  };

  const copyVoucherCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('Copied to clipboard'), { position: toast.POSITION.BOTTOM_RIGHT });
    } catch {
      toast.error(t('An error occured, try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
    }
  };

  const confirmDeleteCode = async () => {
    try {
      await dispatch(deleteVoucherCode({ voucherId, codeId: deleteCode.id })).unwrap();
      toast.success(t('Voucher code deleted successfully'), { position: toast.POSITION.BOTTOM_RIGHT });
      setDeleteCode(null);
    } catch {
      toast.error(t('Used voucher codes cannot be deleted; deactivate the code instead.'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  return (
    <div className="voucher-details">
      <Grid container spacing={2}>
        <Grid item container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h3">{voucher.name}</Typography>
              <Chip
                size="small"
                variant="outlined"
                color={getVoucherStatusColor(voucherStatus)}
                label={t(voucherStatus)}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          </Grid>
          <Grid item>
            <IconButton onClick={(event) => setActionsAnchor(event.currentTarget)}>
              <MoreHorizIcon />
            </IconButton>
            <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setActionsAnchor(null);
                  setEditOpen(true);
                }}
              >
                {t('Edit')}
              </MenuItem>
              <MenuItem onClick={toggleVoucherStatus}>{t(voucher.active ? 'Deactivate' : 'Activate')}</MenuItem>
            </Menu>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 2fr)' },
                gap: 3,
              }}
            >
              <Box
                sx={{
                  borderRight: { lg: 1 },
                  borderBottom: { xs: 1, lg: 0 },
                  borderColor: 'divider',
                  pb: { xs: 3, lg: 0 },
                  pr: { lg: 3 },
                }}
              >
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {t('Campaign')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Detail label={t('Organizer')} value={organizerName} />
                  </Grid>
                  <Grid item xs={12}>
                    <Detail label={t('Discount')} value={formatVoucherDiscount(voucher, t)} />
                  </Grid>
                  <Grid item xs={12}>
                    <Detail label={t('Valid from')} value={formatVoucherDate(voucher.valid_from) || t('Immediately')} />
                  </Grid>
                  <Grid item xs={12}>
                    <Detail
                      label={t('Valid until')}
                      value={formatVoucherDate(voucher.valid_until) || t('No expiry date')}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {t('Eligibility rules')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Detail
                      label={t('Minimum eligible tickets')}
                      value={voucher.min_eligible_ticket_count ?? t('No minimum')}
                      helperText={t('Required eligible ticket count before the voucher activates.')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Detail
                      label={t('Maximum discounted tickets')}
                      value={voucher.max_discounted_ticket_count ?? t('No maximum')}
                      helperText={t('Only this many eligible tickets receive the discount.')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Detail
                      label={t('Minimum ticket price')}
                      value={
                        voucher.min_eligible_ticket_price !== null
                          ? `${Number(voucher.min_eligible_ticket_price).toFixed(2)} Lei`
                          : t('No minimum')
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Detail
                      label={t('Maximum ticket price')}
                      value={
                        voucher.max_eligible_ticket_price !== null
                          ? `${Number(voucher.max_eligible_ticket_price).toFixed(2)} Lei`
                          : t('No maximum')
                      }
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {t('Eligible events')}
                  </Typography>
                  {voucher.event_scope === 'ALL_ORGANIZER_EVENTS' ? (
                    <Box>
                      <Typography>{t('All organizer events')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('Future events are included automatically.')}
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {(voucher.events || []).map((event) => (
                        <ListItem key={event.id} divider disableGutters>
                          <ListItemText primary={event.name} secondary={formatVoucherDate(event.date)} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h5">{`${t('Codes')} (${Number(voucher.code_count)})`}</Typography>
              {!redemptionsCode && (
                <Button variant="contained" onClick={() => setCodeDrawerOpen(true)}>
                  {t('Add code')}
                </Button>
              )}
            </Box>
            <Divider />
            {redemptionsCode ? (
              <Box sx={{ p: 2 }}>
                <VoucherCodeRedemptionsTable
                  voucherId={voucherId}
                  code={redemptionsCode}
                  onBack={() => setRedemptionsCode(null)}
                />
              </Box>
            ) : isCodesLoading ? (
              <Box sx={{ minHeight: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : codes.length ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('Code')}</TableCell>
                      <TableCell>{t('Assigned email')}</TableCell>
                      <TableCell align="right">{t('Redeemed')}</TableCell>
                      <TableCell align="right">{t('Unused')}</TableCell>
                      <TableCell align="center">{t('Status')}</TableCell>
                      <TableCell align="right">{t('Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow
                        key={code.id}
                        hover
                        role="button"
                        tabIndex={0}
                        aria-label={`${t('Code')} ${code.code}`}
                        onClick={() => setRedemptionsCode(code)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setRedemptionsCode(code);
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&:nth-of-type(odd)': (theme) => ({ backgroundColor: theme.palette.action.hover }),
                        }}
                      >
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                            {code.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{code.assignedEmail || '-'}</TableCell>
                        <TableCell align="right">
                          {code.redeemedCount} / {code.maxRedemptions}
                        </TableCell>
                        <TableCell align="right">
                          {Math.max(0, Number(code.maxRedemptions) - Number(code.redeemedCount))}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            variant="outlined"
                            color={code.active ? 'success' : 'default'}
                            label={t(code.active ? 'ACTIVE' : 'INACTIVE')}
                            sx={{ borderRadius: 1 }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <IconButton
                            aria-label={t('Edit')}
                            onClick={() => {
                              setSelectedCode(code);
                              setCodeDrawerOpen(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          {Number(code.reservedCount) === 0 && Number(code.redeemedCount) === 0 && (
                            <IconButton aria-label={t('Delete')} onClick={() => setDeleteCode(code)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                          <IconButton aria-label={t('Copy')} onClick={() => copyVoucherCode(code.code)}>
                            <ContentCopyIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 2 }}>
                <NoData />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      <VoucherDrawer
        open={editOpen}
        voucher={voucher}
        onClose={() => setEditOpen(false)}
        onSaved={() => dispatch(fetchVoucher({ voucherId }))}
      />
      <VoucherCodeDrawer
        open={codeDrawerOpen}
        onClose={handleCodeDrawerClose}
        voucherId={voucherId}
        voucherCode={selectedCode}
      />
      <WarningDialog
        open={Boolean(deleteCode)}
        title={t('Delete voucher code?')}
        content={t('This action is only possible while the code has never been used.')}
        onCancel={() => setDeleteCode(null)}
        onConfirm={confirmDeleteCode}
      />
    </div>
  );
}
